import { debounce } from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { workTitlesAutoComplete } from "../features/customer/customerWorkLocationsSlice";
import { WorkTitlesAutoCompleteResponse } from "../features/customer/types/queries";
import { LanguageSkillCode } from "../features/talents/talentAPI";
import {
  fetchWorkTitles,
  selectAllWorkTitles,
} from "../features/workTitles/workTitleSlice";
import { toSortedWorkTitleOptions } from "../common/helpers/formatData";

export interface WorkTitleOption
  extends Omit<WorkTitlesAutoCompleteResponse, "fieldOfWorkId"> {
  fieldOfWork: string;
}

type Props = {
  groupByFieldOfWork?: boolean;
  fetchAll?: boolean;
};

const useWorkTitleAutocomplete = ({
  groupByFieldOfWork = true,
  fetchAll = false,
}: Props) => {
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [workTitleSearchTerm, setWorkTitleSearchTerm] = useState("");
  const [searchedWorkTitleOptions, setSearchedWorkTitleOptions] = useState<
    WorkTitleOption[]
  >([]);

  const { fieldOfWork } = useAppSelector((state) => state.localized);
  const allWorkTitles = useAppSelector(selectAllWorkTitles);
  const workTitlesStatus = useAppSelector((state) => state.workTitles.status);

  const { i18n } = useTranslation();
  const language = i18n.language.substring(0, 2) as LanguageSkillCode;

  const dispatch = useAppDispatch();
  const serverSearchTerm = fetchAll ? "" : workTitleSearchTerm;

  const debouncedSetWorkTitleSearchTerm = useMemo(
    () =>
      debounce((term: string) => {
        setWorkTitleSearchTerm(term);
      }, 400),
    [],
  );

  const resetSearch = useMemo(
    () => () => {
      debouncedSetWorkTitleSearchTerm.clear();
      setWorkTitleSearchTerm("");
    },
    [debouncedSetWorkTitleSearchTerm],
  );

  useEffect(() => {
    return () => {
      debouncedSetWorkTitleSearchTerm.clear();
    };
  }, [debouncedSetWorkTitleSearchTerm]);

  useEffect(() => {
    if (!fetchAll) return;
    if (workTitlesStatus !== "idle") return;
    dispatch(fetchWorkTitles({ language }));
  }, [fetchAll, workTitlesStatus, language, dispatch]);

  useEffect(() => {
    if (fetchAll) return;
    if (!serverSearchTerm || serverSearchTerm.trim() === "") {
      setIsSearchLoading(false);
      return;
    }

    setIsSearchLoading(true);

    dispatch(
      workTitlesAutoComplete({
        term: serverSearchTerm,
        language,
      }),
    )
      .then((action) => {
        if (isFulfilled(action)) {
          setSearchedWorkTitleOptions(
            toSortedWorkTitleOptions(
              action.payload,
              fieldOfWork || {},
              groupByFieldOfWork,
            ),
          );
        }
      })
      .finally(() => setIsSearchLoading(false));
  }, [
    fetchAll,
    serverSearchTerm,
    dispatch,
    language,
    groupByFieldOfWork,
    fieldOfWork,
  ]);

  const allWorkTitleOptions = useMemo(
    () =>
      toSortedWorkTitleOptions(
        allWorkTitles,
        fieldOfWork || {},
        groupByFieldOfWork,
      ),
    [allWorkTitles, fieldOfWork, groupByFieldOfWork],
  );

  return {
    setWorkTitleSearchTerm: debouncedSetWorkTitleSearchTerm,
    resetSearch,
    workTitleOptions: fetchAll ? allWorkTitleOptions : searchedWorkTitleOptions,
    isLoading: fetchAll ? workTitlesStatus === "pending" : isSearchLoading,
  };
};

export default useWorkTitleAutocomplete;
